import React, { useMemo } from 'react';
import {
  ValueAxis,
  ArgumentAxis,
  Series,
  Legend,
  CommonSeriesSettings,
} from 'devextreme-react/chart';
import { NuvoBarChart } from '@nuvolo/nuux-chart/components/NuvoBarChart';
import { DashboardStatsPoint } from '#/graphql/types';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import { NuvoText } from '@nuvolo/nuux-core/components/NuvoText';
import FAIcon from '#/common/components/FAIcon';

export interface BarLineChartProps {
  title?: string;
  noDataText?: string;
  dataSource: { key: string; statsPoints: DashboardStatsPoint[] }[] | undefined | null;
  setup: {
    valueAxis: string;
    lineAxis: string;
    argumentAxis: string;
  };
  barColor?: string;
  lineColor?: string;
}

const BarLineChart: React.FC<BarLineChartProps> = ({
  title,
  noDataText = 'No data to display!',
  dataSource,
  setup,
  barColor = '#1D8AFE',
  lineColor = '#FC533E',
}) => {
  const { ds, barField, lineField, barLabel, lineLabel } = useMemo(() => {
    if (!dataSource?.length) {
      return { ds: [], barField: '', lineField: '', barLabel: '', lineLabel: '' };
    }

    const [bar, line] = dataSource[0].statsPoints;

    const rows = dataSource.map((entry) => {
      const [b, l] = entry.statsPoints;
      return {
        key: entry.key,
        [bar?.filterByValue ?? 'bar']: b?.value ?? 0,
        [line?.filterByValue ?? 'line']: l?.value ?? 0,
      };
    });

    return {
      ds: rows,
      barField: bar?.filterByValue ?? 'bar',
      lineField: line?.filterByValue ?? 'line',
      barLabel: bar?.label ?? '',
      lineLabel: line?.label ?? '',
    };
  }, [dataSource]);

  const hasData = ds && ds.length > 0;
  if (!hasData) {
    return (
      <NuvoBox
        height="100%"
        minHeight="300px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        flex={1}>
        <FAIcon icon="shapes" size="xl" />
        <NuvoText type="body">{noDataText}</NuvoText>
      </NuvoBox>
    );
  }

  return (
    <NuvoBarChart showTooltip title={title} dataSource={ds}>
      <CommonSeriesSettings argumentField="key" />
      <ArgumentAxis
        title={setup.argumentAxis}
        label={{ rotationAngle: 45, indentFromAxis: 0, overlappingBehavior: 'rotate' }}
      />
      <ValueAxis name="bar" title={setup.valueAxis} position="left" />
      <ValueAxis name="line" title={setup.lineAxis} position="right" />
      <Series type="bar" valueField={barField} name={barLabel} axis="bar" color={barColor} />
      <Series
        type="line"
        valueField={lineField}
        name={lineLabel}
        axis="line"
        color={lineColor}
        point={{ visible: true, size: 6 }}
      />
      <Legend
        visible
        verticalAlignment="bottom"
        horizontalAlignment="center"
        itemTextPosition="right"
      />
    </NuvoBarChart>
  );
};

export default React.memo(BarLineChart);
